/** @jsxImportSource @emotion/react */
import { Header, useSession } from '@quickbit/backoffice-core-frontend';
import {
  SearchInput,
  Typography,
  useForm,
  useTheme,
  UtilitySelect,
  Option,
  DateInput,
  Table,
  Pagination,
  UtilityButton,
  Icon,
  Card,
} from '@quickbit/qb-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'react-use';
import {
  Filters,
  TransactionRequestState,
  useSearch,
} from 'api/useDirectOnrampApi';
import { QBLoader } from 'components';
import { BatchActions } from './BatchActions';
import { StartDirectOnrampFlow } from './StartDirectOnrampFlow';

type FilterStore = {
  filters: Filters;
  pagination: {
    page: number;
    size: number;
  };
} | null;

const PAGE_SIZE = 16;
const DEFAULT_FROM = new Date();
DEFAULT_FROM.setDate(DEFAULT_FROM.getDate() - 3);

const buildDefaultFilterFormValues = (store: FilterStore) =>
  ({
    emailAddress: store?.filters.emailAddress || null,
    referenceId: store?.filters.referenceId || null,
    euroAmount: store?.filters.euroAmount || null,
    states: store?.filters.states || null,
    purchasedAtGt: store?.filters.purchasedAtGt
      ? new Date(store.filters.purchasedAtGt)
      : DEFAULT_FROM,
    purchasedAtLt: store?.filters.purchasedAtLt
      ? new Date(store.filters.purchasedAtLt)
      : null,
    brokerTransactionCode: store?.filters.brokerTransactionCode || null,
    custodianTransactionCode: store?.filters.custodianTransactionCode || null,
    blockchainTransactionId: store?.filters.blockchainTransactionId || null,
  } as Filters);

const STATE_FILTER_OPTIONS: {
  value: TransactionRequestState;
  label: string;
}[] = Object.values(TransactionRequestState).map((s) => ({
  value: s,
  label: s,
}));

export const TransactionRequestOverview = () => {
  const { spacing, palette, sizing } = useTheme();
  const navigate = useNavigate();
  const session = useSession();
  const [batchSelected, setBatchSelected] = useState<Set<string>>(new Set());

  const [filterStore, setFilterStore] = useSessionStorage<FilterStore>(
    'qbDirectOnrampTxReqFilterStore'
  );

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<Filters>(
    buildDefaultFilterFormValues(filterStore),
    {}
  );

  useEffect(() => {
    setFilterStore({
      filters: fields,
      pagination: { page, size: PAGE_SIZE },
    });
  }, [fields, page, setFilterStore]);

  const handleResetFilters = () => {
    setPage(1);
    setters.set(
      buildDefaultFilterFormValues({
        filters: {},
        pagination: { page: 1, size: PAGE_SIZE },
      })
    );
    setBatchSelected(new Set());
  };

  const headers = [
    { label: 'Purchased At', cellKey: 'purchasedAt', sort: false },
    { label: 'Reference ID', cellKey: 'referenceId', sort: false },
    { label: 'Email Address', cellKey: 'emailAddress', sort: false },
    { label: 'Euro Amount', cellKey: 'euroAmount', sort: false },
    {
      label: 'State',
      cellKey: 'state',
      sort: false,
    },
  ];

  const { data, isFetching, refetch } = useSearch(fields, {
    page,
    size: PAGE_SIZE,
  });

  const rows = useMemo(() => {
    if (!data) return [];

    // A workaround to reset pagination if the data rows are less than the page size
    setPage((state) => (data.aggregatedData.count <= PAGE_SIZE ? 1 : state));

    return Object.values(data.transactionRequests).map((tx) => ({
      id: tx.referenceId,
      value: {
        ...tx,
        purchasedAt: tx.purchasedAt.toLocaleString(),
        createdAt: tx.createdAt.toLocaleString(),
      },
    }));
  }, [data]);
  if (!data) return <QBLoader />;

  const canEdit =
    session.authenticated &&
    session.permissions.includes('edit_merchant_orders');
  const canStartDirectOnrampFlow =
    canEdit && session.permissions.includes('start_test_direct_onramp');

  return (
    <>
      <Header>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle">Transaction Requests</Typography>
          {canStartDirectOnrampFlow && (
            <StartDirectOnrampFlow onCreate={refetch} />
          )}
        </div>
      </Header>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(8, auto)',
          gap: spacing.xxxs,
          alignItems: 'center',
        }}
      >
        <SearchInput
          placeholder="Reference ID"
          value={fields.referenceId ?? ''}
          onChange={(e) => setters.referenceId?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Email Address"
          value={fields.emailAddress ?? ''}
          onChange={(e) => setters.emailAddress?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Euro Amount"
          value={fields.euroAmount?.toString() ?? ''}
          onChange={(e) =>
            setters.euroAmount?.set(
              e.target.value.replace(',', '.') || undefined
            )
          }
        />
        <SearchInput
          placeholder="Broker Transaction Code"
          value={fields.brokerTransactionCode ?? ''}
          onChange={(e) => setters.brokerTransactionCode?.set(e.target.value)}
        />
        <SearchInput
          placeholder="Custodian Transaction Code"
          value={fields.custodianTransactionCode ?? ''}
          onChange={(e) =>
            setters.custodianTransactionCode?.set(e.target.value)
          }
        />
        <SearchInput
          placeholder="Blockchain Transaction ID"
          value={fields.blockchainTransactionId ?? ''}
          onChange={(e) => setters.blockchainTransactionId?.set(e.target.value)}
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: '8fr 1fr',
          textWrap: 'nowrap',
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xxxs,
            flexWrap: 'wrap',
          }}
        >
          <UtilitySelect
            label={
              fields.states && fields.states.length > 0
                ? `States (${fields.states.length})`
                : 'All States'
            }
            multiple
            listTitle="States"
            options={STATE_FILTER_OPTIONS}
            value={
              fields.states
                ? STATE_FILTER_OPTIONS.filter((stateOption) =>
                    fields.states?.includes(stateOption.value)
                  )
                : null
            }
            onSelect={(data) => {
              setPage(1);
              setters.states?.set(
                data
                  ? (data as Option[]).map(
                      (option) => option.value as TransactionRequestState
                    )
                  : []
              );
            }}
          />
          <DateInput
            placeholder="All time"
            type="range"
            value={[
              fields.purchasedAtGt ? new Date(fields.purchasedAtGt) : null,
              fields.purchasedAtLt ? new Date(fields.purchasedAtLt) : null,
            ]}
            onChange={(e: [Date | null, Date | null]) => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              setters.purchasedAtGt?.set(e[0]!);

              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              setters.purchasedAtLt?.set(e[1]!);
            }}
            withTime
          />
          <UtilityButton
            variant="text"
            text="Reset filters"
            onClick={handleResetFilters}
          />
          <UtilityButton
            variant="secondary"
            text="Refresh"
            onClick={() => refetch()}
            disabled={isFetching}
          />
          {isFetching && (
            <Icon
              name="spinner"
              className={{ svg: { color: palette.grey.default } }}
            />
          )}
        </div>
        <div
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'end',
          }}
        >
          {batchSelected.size > 0 && (
            <div>
              <UtilityButton
                text="Batch Actions"
                dropdown={
                  <Card css={{ minWidth: sizing.medium }}>
                    <BatchActions
                      transactionRequests={data.transactionRequests.filter(
                        (t) => batchSelected.has(t.referenceId)
                      )}
                    />
                  </Card>
                }
              />
            </div>
          )}
        </div>
      </div>
      <div css={{ marginTop: spacing.xxs }}>
        <Table
          headers={headers}
          rows={rows}
          onRowClick={(r) =>
            // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
            navigate(`/direct-onramp/transaction-requests/${r.referenceId}`)
          }
          selected={canEdit ? batchSelected : undefined}
          updateSelected={canEdit ? setBatchSelected : undefined}
        />
      </div>
      <div css={{ marginTop: spacing.xxs }}>
        <Pagination
          currentPage={page}
          onChange={setPage}
          numberOfElements={data.aggregatedData.count}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};
